import { SIGNUP_SOURCE_LOCAL_STORAGE_KEY } from '@common/constants'
import { CClient } from '@common/utils'
import {
  generateUTMParametersDictFromCookie,
  getClickIDParametersDictFromCookie,
  getUTMParametersDictFromLocalStorage,
} from '@lib/analytics/track-utm'
import {
  MASKED_EMAIL,
  USER_COUNTRY_FROM_IP_COOKIE,
} from '@lib/constants/common'
import { SIGNUP_REFERRER_LOCAL_STORAGE_KEY } from '@lib/constants/tracking'
import { ROLES } from '@lib/enums/dashboard'
import { SYSTEM_TIMEZONE } from '@lib/utils/date-native'
import {
  getFromLocalStorage,
  setToLocalStorage,
} from '@lib/utils/local-storage'
import { buildIdentifierBody, IdentifierMode } from '@lib/utils/identifier'
import { checkSupportedDevice } from '@lib/utils/system-check'
import { captureMessage } from '@sentry/node'
import Router from 'next/router'
import { parseCookies } from 'nookies'
import platform from 'platform'

const MAX_UA_LENGTH = 1024

export const registerUser = async (formBody) => {
  const url = '/api/v1/users/'
  // The country MaxMind resolved from the IP during SSR. Sent here rather than
  // from each caller because all five signup flows go through this function,
  // and the backend uses it to work out where the user is - a phone's dial code
  // says where a SIM was bought, not where its owner lives.
  const countryFromIP = parseCookies()?.[USER_COUNTRY_FROM_IP_COOKIE]
  const result = CClient(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(
      countryFromIP
        ? { ...formBody, countryCode: countryFromIP.toUpperCase() }
        : formBody
    ),
  })
  return result
}

export const requestLoginOtp = async (
  formBody,
  viaCall = false,
  viaWhatsapp = false,
  viaEmail = false,
  viaSms = false
) => {
  let url = '/api/v1/auth/phone/'
  if (viaCall) url += '?via=call'
  if (viaWhatsapp) url += '?via=whatsapp'
  if (viaEmail) url += '?via=email'
  if (viaSms) url += '?via=sms'

  const result = await CClient(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(formBody),
  })

  if (result.status === 200) {
    const resultData = await result.json()
    if (resultData.email) {
      setToLocalStorage(MASKED_EMAIL, String(resultData.email), () => {
        console.error('error saving maskedEmail to localstorage')
      })
    } else {
      setToLocalStorage(MASKED_EMAIL, 'none', () => {
        console.error('error saving maskedEmail to localstorage')
      })
    }
  }

  return result
}

export const verifyOtpAndLogin = async (formBody) => {
  const url = '/api/v1/auth/token/'
  const result = await CClient(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(formBody),
  })

  return result
}

export const validateForm = () => {
  return {}
}

// TODO: Update type of keys
export const prepareRegistrationFormBody = (data: any) => {
  const {
    phone,
    identifierMode = 'phone' as IdentifierMode,
    name,
    email,
    role,
    childGrade,
    childGender,
    parentName,
    hasLaptop,
    teacherEducationalQualification,
    teacherExperience,
    teacherGender,
    whatsappEnabled,
    voucher,
  } = data
  // An email signup sends no phone at all - the backend allocates a placeholder
  // one, and the real number is collected after the trial is booked.
  const formBody = {
    name: name,
    email: email,
    ...buildIdentifierBody(phone, identifierMode),
    getOtp: true,
    preferences: {
      whatsappEnabled: whatsappEnabled,
    },
  }

  const referralCampaign = getFromLocalStorage('referralCampaign')
  const referrerUsername = getFromLocalStorage('referrerUsername')
  // send lead source information
  let leadSourceDict = getUTMParametersDictFromLocalStorage()
  if (!leadSourceDict) leadSourceDict = generateUTMParametersDictFromCookie()
  const clickIDs = getClickIDParametersDictFromCookie()
  formBody['leadSource'] = { ...leadSourceDict, ...clickIDs }
  formBody['signupReferer'] =
    getFromLocalStorage(SIGNUP_REFERRER_LOCAL_STORAGE_KEY) || ''
  formBody['signupSource'] = data.signupSource
    ? data.signupSource
    : getFromLocalStorage(SIGNUP_SOURCE_LOCAL_STORAGE_KEY) ||
      document.location.pathname

  if (role === ROLES.TEACHER) {
    const teacher = {
      experience: teacherExperience.value,
      educationalQualification: teacherEducationalQualification.value,
    }
    formBody['teacher'] = teacher
    formBody['gender'] = teacherGender.value
  }
  if (role === ROLES.STUDENT) {
    formBody['student'] = {
      grade: childGrade.value,
      parentName: parentName,
    }
    formBody['gender'] = childGender.value
    // Device suitability is no longer asked in the pre-signup form. If an explicit
    // answer was forwarded in the URL (e.g. the web-static landing pages ask the
    // question and pass ?hasLaptop=true|false), honor it. Otherwise seed from the
    // device: desktop => true, mobile => false (so mobile users don't inherit the
    // backend default of True and skip the post-signup device-confirmation gate).
    // Mobile users confirm their real answer at /trial-class/system-confirmation.
    formBody['hasLaptop'] =
      typeof hasLaptop === 'boolean'
        ? hasLaptop
        : checkSupportedDevice().device === 'desktop'
    formBody['voucher'] = voucher
  }

  formBody['timezone'] = SYSTEM_TIMEZONE || ''
  let userAgent = platform.ua
  if (userAgent.length > MAX_UA_LENGTH) {
    captureMessage(`user agent not captured for ${email}: ${userAgent}`)
    userAgent = ''
  }
  formBody['signupUserAgent'] = userAgent
  formBody['referrerUsername'] = referrerUsername || null
  formBody['referralCampaignSlug'] = referralCampaign || null
  return formBody
}

export const handleLogout = () => {
  Router.push('/logout/?next=' + Router.asPath)
}
